<script setup lang="ts">
import ProjectNoticeDialog from '@/components/projects/ProjectNoticeDialog.vue';
import PublishProjectDialog from '@/components/projects/PublishProjectDialog.vue';
import ProjectSummaryCard from '@/components/projects/ProjectSummaryCard.vue';
import FeatureGuideDialog from '@/components/workspace/FeatureGuideDialog.vue';
import FeatureGuideIllustration from '@/components/workspace/FeatureGuideIllustration.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { EmptyState } from '@/components/ui/empty-state';
import SearchableSelectMenu from '@/components/ui/searchable-select-menu/SearchableSelectMenu.vue';
import GroupedEntityList from '@/components/workspace/GroupedEntityList.vue';
import MetricCard from '@/components/workspace/MetricCard.vue';
import SectionHeader from '@/components/workspace/SectionHeader.vue';
import { useProjectQuickCreate } from '@/composables/projects/useProjectQuickCreate';
import { CalendarRange, CheckCircle2, ChevronDown, ChevronRight, CircleHelp, Clock3, FolderOpen, FolderPlus, GripVertical, Layers3, ListChecks, Rocket } from 'lucide-vue-next';
import type { FeatureGuideStep } from '~/types/guidedSetup';
import { clientOwnershipLabel, resolveOwnedClientId } from '~/lib/clientOwnership';
import { getProjectStatusOption, getTenantProjectStatuses } from '~/lib/projectConfig';
import { projectSettingsPath } from '~/lib/projectEditing';
import { filterEntitiesForProjectScope } from '~/lib/projectEntityScope';
import { tagKey } from '~/lib/entityTags';
import { isProjectArchived, isProjectCompleted, isProjectOperational } from '~/lib/projectLifecycle';

definePageMeta({
    layout: 'app',
    middleware: ['tenant']
});

const route = useRoute();
const router = useRouter();
const projectStore = useProjectStore();
const clientStore = useClientStore();
const taskStore = useTaskStore();
const userStore = useUserStore();
const tenantStore = useTenantStore();
const approvalsStore = useApprovalsStore();
const commentsStore = useCommentsStore();
const filesStore = useFilesStore();
const entityTagsStore = useEntityTagsStore();
const { canMutate } = usePermissions();
const subscription = useWorkspaceSubscription();
const { openProjectQuickCreate } = useProjectQuickCreate();
const runtimeConfig = useRuntimeConfig();
const projectGuide = useFeatureGuideProgress('project-creation-introduction');

const isPublishDialogOpen = ref(false);
const isNoticeDialogOpen = ref(false);
const publishTargetProject = ref<any>(null);
const noticeTargetProject = ref<any>(null);
const collapsedProjectGroupIds = ref<Set<string>>(new Set());
const draggingProjectGroupId = ref<string | null>(null);
const projectGroupDropTargetId = ref<string | null>(null);
const projectGuideVideoUrl = computed(() => String(runtimeConfig.public.projectGuideVideoUrl || '').trim());
const projectGuideSteps = computed<FeatureGuideStep[]>(() => [
    {
        id: 'outcome',
        eyebrow: 'The outcome',
        title: 'Create a clear home for the work',
        description: 'A project brings the client, delivery structure, tasks, dates, files, approvals, and communication together around one shared outcome.',
        points: [
            'Connect the work to the right client from the beginning.',
            'Give the team one place to understand delivery progress.',
            'Expose only the right information to the client portal.',
        ],
        ...(projectGuideVideoUrl.value
            ? { video: { src: projectGuideVideoUrl.value, title: 'How projects organise client delivery' } }
            : {}),
    },
    {
        id: 'foundation',
        eyebrow: 'Starting point',
        title: 'Choose how much structure to inherit',
        description: 'Start blank for one-off work, apply a workflow for repeatable delivery, or use a project template when the service also has reusable project and commercial defaults.',
        points: [
            'Use blank only when the work genuinely has no repeatable shape.',
            'Use a workflow when milestones and starter tasks are enough.',
            'Use a project template for a packaged or frequently repeated offer.',
        ],
    },
    {
        id: 'plan',
        eyebrow: 'Delivery plan',
        title: 'Add only the tasks and dates this project needs',
        description: 'Inherited work can be supplemented with one-off tasks, then placed against a realistic schedule before the project is created.',
        points: [
            'Keep one-off work separate from reusable workflow tasks.',
            'Set dates when they are meaningful, or schedule later.',
            'Review milestones and task visibility before launch.',
        ],
    },
    {
        id: 'create',
        eyebrow: 'Create with guidance',
        title: 'Build the project in five manageable decisions',
        description: 'The guided creator covers project basics, starting structure, additional tasks, schedule, and a final review. Optional steps can be skipped without losing progress.',
        points: [
            'Complete the essentials first.',
            'Skip additional tasks or scheduling when they are not ready.',
            'Check the complete project summary before creating it.',
        ],
    },
]);
const projectGuideVisuals = {
    outcome: { icon: FolderOpen, label: 'One clear home for client delivery' },
    foundation: { icon: Layers3, label: 'The right amount of reusable structure' },
    plan: { icon: CalendarRange, label: 'Work and dates shaped for this project' },
    create: { icon: Rocket, label: 'Review, create, and begin delivery' },
} as const;

function projectGuideVisual(stepId: string) {
    return projectGuideVisuals[stepId as keyof typeof projectGuideVisuals]
        || { icon: ListChecks, label: 'A guided project setup' };
}

const tenantSlug = computed(() => {
    const routeTenantSlug = route.params.tenantSlug;
    return typeof routeTenantSlug === 'string' ? routeTenantSlug : '';
});
const { tenantPath } = useTenantPath();
type ProjectGroupBy = 'none' | 'status' | 'client' | 'milestone' | 'tag';
const projectGroupByOptions: Array<{ value: ProjectGroupBy; label: string }> = [
    { value: 'none', label: 'No grouping' },
    { value: 'status', label: 'Status' },
    { value: 'client', label: 'Client' },
    { value: 'milestone', label: 'Milestone' },
    { value: 'tag', label: 'Tag' },
];
const validProjectGroupBy = new Set<ProjectGroupBy>(projectGroupByOptions.map(option => option.value));
const projectGroupBy = computed<ProjectGroupBy>(() => {
    const value = String(route.query.groupBy || '').trim().toLowerCase();
    return validProjectGroupBy.has(value as ProjectGroupBy) ? (value as ProjectGroupBy) : 'none';
});
const projectGroupStorageKey = computed(() => (
    tenantSlug.value
        ? `project-table:group-by:${tenantSlug.value}`
        : 'project-table:group-by'
));
const projectGroupCollapseStorageKey = computed(() => `${projectGroupStorageKey.value}:collapsed:${projectGroupBy.value}`);
const attentionFilter = computed(() => String(route.query.attention || '').trim().toLowerCase());
const projectSearchQuery = ref('');
const projectTagFilter = ref('');
const projectTagOptions = computed(() => entityTagsStore.tagsForType('project').map(tag => ({ value: tag, label: tag })));
type ProjectView = 'active' | 'completed' | 'archived' | 'all';
const projectViewOptions: Array<{ value: ProjectView, label: string }> = [
    { value: 'active', label: 'Active projects' },
    { value: 'completed', label: 'Completed projects' },
    { value: 'archived', label: 'Archived projects' },
    { value: 'all', label: 'All projects' },
];
const projectView = computed<ProjectView>(() => {
    const value = String(route.query.view || 'active').trim().toLowerCase();
    return projectViewOptions.some(option => option.value === value) ? value as ProjectView : 'active';
});
const hasProjectFilters = computed(() => Boolean(projectSearchQuery.value.trim() || projectTagFilter.value));
const currentProjectGroupingPreference = computed(() => {
    const value = String(userStore.user?.preferences?.tasks?.currentProjectGrouping || '').trim().toLowerCase();
    return validProjectGroupBy.has(value as ProjectGroupBy) ? (value as ProjectGroupBy) : null;
});
const projectGroupOrderOverrides = ref<Partial<Record<ProjectGroupBy, string[]>>>({});
const projectGroupOrderPreference = computed(() => (
    projectGroupBy.value === 'none'
        ? []
        : (projectGroupOrderOverrides.value[projectGroupBy.value] || userStore.user?.preferences?.tasks?.projectGroupOrders?.[projectGroupBy.value] || [])
));

onMounted(async () => {
    await restoreProjectGrouping();
    loadCollapsedProjectGroups();

    await Promise.all([
        projectStore.fetchProjects(),
        clientStore.fetchClients(),
        taskStore.fetchTasks(),
        entityTagsStore.fetchEntityTags(),
    ]);

    const projectIds = projectStore.projects.map((project) => project.id);
    await Promise.all([
        approvalsStore.fetchApprovalsForProjects(projectIds),
        commentsStore.fetchCommentsForEntities('project', projectIds),
        filesStore.fetchFilesForEntities('project', projectIds),
    ]);

    projectGuide.offerIfEligible(canCreateProjects.value && projectStore.projects.length === 0);
});

const doneStatuses = new Set(['done', 'completed']);

function resolveClientAvatar(project: any): string {
    const resolvedClientId = resolveOwnedClientId(project);
    if (resolvedClientId) {
        const byId = clientStore.clients.find((client) => client.id === resolvedClientId);
        if (byId?.avatarURL) return byId.avatarURL;
    }

    const clientName = String(project.clientName || project.client || '').trim().toLowerCase();
    if (!clientName) return '';

    const byName = clientStore.clients.find((client) => {
        const labels = [client.companyName, client.name]
            .filter(Boolean)
            .map((value) => String(value).trim().toLowerCase());
        return labels.includes(clientName);
    });

    return byName?.avatarURL || '';
}

const clientAvatarByProjectId = computed(() => Object.fromEntries(
    projectStore.projects.map((project) => [project.id, resolveClientAvatar(project)]),
));
const clientNameById = computed(() => Object.fromEntries(
    clientStore.clients.map((client) => [client.id, client.companyName || client.name || client.id]),
));

const projectStatsById = computed(() => {
    const stats: Record<string, {
        taskTotal: number,
        taskCompleted: number,
        commentsCount: number,
        filesCount: number,
        pendingApprovals: number,
    }> = {};

    for (const project of projectStore.projects) {
        const projectTasks = filterEntitiesForProjectScope(taskStore.tasks, project.id, project.clientId);
        const taskCompleted = projectTasks.filter((task) => doneStatuses.has(String(task.status || '').toLowerCase())).length;
        const commentsCount = commentsStore.commentsByEntity[`project:${project.id}`]?.length || 0;
        const filesCount = filesStore.filesByEntity[`project:${project.id}`]?.length || 0;
        const pendingApprovals = filterEntitiesForProjectScope(
            approvalsStore.approvalsByProject[project.id] || [],
            project.id,
            project.clientId,
        )
            .filter((approval) => approval.status === 'pending')
            .length;

        stats[project.id] = {
            taskTotal: projectTasks.length,
            taskCompleted,
            commentsCount,
            filesCount,
            pendingApprovals,
        };
    }

    return stats;
});

const activeProjectsCount = computed(() => {
    return projectStore.projects.filter(project => isProjectOperational(project.status)).length;
});

const tasksDueSoonCount = computed(() => {
    const nowMs = Date.now();
    const sevenDaysMs = nowMs + 7 * 24 * 60 * 60 * 1000;
    return taskStore.tasks.filter(task => {
        const project = projectStore.projects.find(item => item.id === task.projectId);
        if (project && !isProjectOperational(project.status)) return false;
        if (!task.dueDate || task.status === 'Done') return false;
        const dueMs = new Date(task.dueDate).getTime();
        return dueMs <= sevenDaysMs;
    }).length;
});

const approvalsWaitingCount = computed(() => {
    return projectStore.projects.filter(project => isProjectOperational(project.status)).reduce((total, project) => {
        const approvals = approvalsStore.approvalsByProject[project.id] || [];
        const pending = approvals.filter((approval) => approval.status === 'pending').length;
        return total + pending;
    }, 0);
});

const filteredProjects = computed(() => {
    let projects = projectStore.projects.filter((project) => {
        if (projectView.value === 'archived') return isProjectArchived(project.status);
        if (projectView.value === 'completed') return isProjectCompleted(project.status);
        if (projectView.value === 'all') return true;
        return isProjectOperational(project.status);
    });
    if (attentionFilter.value === 'approvals') {
        projects = projects.filter(project => (projectStatsById.value[project.id]?.pendingApprovals || 0) > 0);
    }

    const query = projectSearchQuery.value.trim().toLowerCase();
    if (query) {
        projects = projects.filter(project => [
            project.name,
            project.description,
            project.clientName,
            project.client,
            project.stage,
            ...entityTagsStore.tagsFor('project', project.id),
        ].some(value => String(value || '').toLowerCase().includes(query)));
    }

    if (projectTagFilter.value) {
        projects = projects.filter(project => entityTagsStore.hasTag('project', project.id, projectTagFilter.value));
    }

    return projects;
});

function clearProjectFilters() {
    projectSearchQuery.value = '';
    projectTagFilter.value = '';
}

const groupedProjectSections = computed(() => {
    const sections = new Map<string, {
        id: string,
        title: string,
        projects: typeof projectStore.projects,
    }>();
    const configuredStatuses = getTenantProjectStatuses(tenantStore.currentTenant).map(status => status.label);

    for (const project of filteredProjects.value) {
        let groups = [{ id: 'all-projects', title: 'All projects' }];

        if (projectGroupBy.value === 'status') {
            const status = String(project.status || '').trim() || 'No status';
            groups = [{ id: status.toLowerCase().replace(/\s+/g, '-'), title: status }];
        } else if (projectGroupBy.value === 'milestone') {
            const stage = String(project.stage || '').trim() || 'No milestone';
            groups = [{ id: `milestone:${stage.toLowerCase().replace(/\s+/g, '-')}`, title: stage }];
        } else if (projectGroupBy.value === 'client') {
            const entityId = resolveOwnedClientId(project);
            const entityLabel = clientOwnershipLabel(project, {
                clientNameById: clientNameById.value,
            });
            groups = [{ id: entityId || entityLabel || 'no-client', title: entityLabel || 'No linked client' }];
        } else if (projectGroupBy.value === 'tag') {
            const tags = entityTagsStore.tagsFor('project', project.id);
            groups = tags.length
                ? tags.map(tag => ({ id: `tag:${tagKey(tag)}`, title: tag }))
                : [{ id: 'no-tag', title: 'No tag' }];
        }

        for (const group of groups) {
            const existing = sections.get(group.id) || {
                ...group,
                projects: [],
            };
            existing.projects.push(project);
            sections.set(group.id, existing);
        }
    }

    const ordered = Array.from(sections.values());

    if (projectGroupBy.value === 'none') {
        return ordered;
    }

    if (projectGroupBy.value === 'status') {
        return applyProjectGroupOrderPreference(ordered.sort((a, b) => {
        const aIndex = configuredStatuses.indexOf(a.title);
        const bIndex = configuredStatuses.indexOf(b.title);
        if (aIndex !== -1 || bIndex !== -1) {
            if (aIndex === -1) return 1;
            if (bIndex === -1) return -1;
            return aIndex - bIndex;
        }
        return a.title.localeCompare(b.title);
        }));
    }

    if (projectGroupBy.value === 'milestone') {
        return applyProjectGroupOrderPreference(ordered.sort((a, b) => {
            if (a.title === 'No milestone') return 1;
            if (b.title === 'No milestone') return -1;
            return a.title.localeCompare(b.title);
        }));
    }

    if (projectGroupBy.value === 'tag') {
        return applyProjectGroupOrderPreference(ordered.sort((a, b) => {
            if (a.id === 'no-tag') return 1;
            if (b.id === 'no-tag') return -1;
            return a.title.localeCompare(b.title);
        }));
    }

    return applyProjectGroupOrderPreference(ordered.sort((a, b) => {
        if (a.title === 'No linked client') return 1;
        if (b.title === 'No linked client') return -1;
        return a.title.localeCompare(b.title);
    }));
});

function projectGroupColor(section: { title: string }): string {
    if (projectGroupBy.value === 'tag') return section.title === 'No tag'
        ? 'var(--muted-foreground)'
        : entityTagsStore.colorFor(section.title);
    if (projectGroupBy.value !== 'status') return 'var(--primary)';

    return getProjectStatusOption(
        section.title,
        getTenantProjectStatuses(tenantStore.currentTenant),
    )?.color || 'var(--primary)';
}

function applyProjectGroupOrderPreference<T extends { id: string }>(sections: T[]): T[] {
    const preferredOrder = projectGroupOrderPreference.value;
    if (!preferredOrder.length) return sections;

    const orderMap = new Map(preferredOrder.map((id, index) => [id, index]));
    return [...sections].sort((a, b) => {
        const aIndex = orderMap.get(a.id);
        const bIndex = orderMap.get(b.id);
        if (aIndex === undefined && bIndex === undefined) return 0;
        if (aIndex === undefined) return 1;
        if (bIndex === undefined) return -1;
        return aIndex - bIndex;
    });
}

async function updateProjectGrouping(value: string) {
    const next = validProjectGroupBy.has(value as ProjectGroupBy) ? (value as ProjectGroupBy) : 'none';
    const query = { ...route.query };
    if (next === 'none') delete query.groupBy;
    else query.groupBy = next;
    persistProjectGrouping(next);
    await router.replace({ path: route.path, query });
    await userStore.updateTaskPreferences({
        currentProjectGrouping: next,
    });
}

function persistProjectGrouping(groupBy: ProjectGroupBy) {
    if (typeof window === 'undefined') return;
    try {
        if (groupBy === 'none') window.localStorage.removeItem(projectGroupStorageKey.value);
        else window.localStorage.setItem(projectGroupStorageKey.value, groupBy);
    } catch {
        // Local persistence is optional.
    }
}

async function restoreProjectGrouping() {
    if (typeof window === 'undefined' || typeof route.query.groupBy === 'string') {
        if (typeof route.query.groupBy === 'string') persistProjectGrouping(projectGroupBy.value);
        return;
    }

    try {
        const stored = String(window.localStorage.getItem(projectGroupStorageKey.value) || '').trim();
        const preferredGrouping = String(userStore.user?.preferences?.tasks?.defaultProjectGrouping || '').trim().toLowerCase();
        const nextGrouping = currentProjectGroupingPreference.value
            ? currentProjectGroupingPreference.value
            : validProjectGroupBy.has(stored as ProjectGroupBy) && stored !== 'none'
            ? stored
            : validProjectGroupBy.has(preferredGrouping as ProjectGroupBy) && preferredGrouping !== 'none'
            ? preferredGrouping
            : '';
        if (!nextGrouping || nextGrouping === 'none') return;
        await router.replace({ path: route.path, query: { ...route.query, groupBy: nextGrouping } });
    } catch {
        // Ignore invalid stored state and keep the default view.
    }
}

async function persistProjectGroupOrder(order: string[]) {
    if (projectGroupBy.value === 'none') return;
    projectGroupOrderOverrides.value = {
        ...projectGroupOrderOverrides.value,
        [projectGroupBy.value]: order,
    };
    await userStore.updateTaskPreferences({
        projectGroupOrders: {
            ...(userStore.user?.preferences?.tasks?.projectGroupOrders || {}),
            [projectGroupBy.value]: order,
        },
    });
}

function startProjectGroupDrag(groupId: string) {
    draggingProjectGroupId.value = groupId;
    projectGroupDropTargetId.value = null;
}

function setProjectGroupDropTarget(groupId: string) {
    if (!draggingProjectGroupId.value || draggingProjectGroupId.value === groupId) return;
    projectGroupDropTargetId.value = groupId;
}

async function handleProjectGroupDrop(targetGroupId: string) {
    const sourceGroupId = draggingProjectGroupId.value;
    draggingProjectGroupId.value = null;
    projectGroupDropTargetId.value = null;

    if (!sourceGroupId || sourceGroupId === targetGroupId || projectGroupBy.value === 'none') {
        return;
    }

    const currentOrder = groupedProjectSections.value.map((section) => section.id);
    const fromIndex = currentOrder.indexOf(sourceGroupId);
    const toIndex = currentOrder.indexOf(targetGroupId);
    if (fromIndex < 0 || toIndex < 0) return;

    const nextOrder = [...currentOrder];
    const [moved] = nextOrder.splice(fromIndex, 1);
    if (!moved) return;
    nextOrder.splice(toIndex, 0, moved);

    await persistProjectGroupOrder(nextOrder);
}

async function applyUserDefaultProjectGrouping(groupingValue?: string | null) {
    if (typeof window === 'undefined' || typeof route.query.groupBy === 'string') return;

    try {
        const stored = String(window.localStorage.getItem(projectGroupStorageKey.value) || '').trim();
        if (validProjectGroupBy.has(stored as ProjectGroupBy) && stored !== 'none') {
            return;
        }
    } catch {
        // Ignore invalid stored state and fall through to user defaults.
    }

    const preferredGrouping = String(groupingValue || '').trim().toLowerCase();
    if (!validProjectGroupBy.has(preferredGrouping as ProjectGroupBy)) {
        return;
    }

    if (projectGroupBy.value === preferredGrouping) {
        return;
    }

    const query = { ...route.query };
    if (preferredGrouping === 'none') delete query.groupBy;
    else query.groupBy = preferredGrouping;
    await router.replace({ path: route.path, query });
}

function isProjectGroupCollapsed(groupId: string) {
    return collapsedProjectGroupIds.value.has(groupId);
}

function loadCollapsedProjectGroups() {
    if (typeof window === 'undefined') return;
    try {
        const parsed = JSON.parse(window.localStorage.getItem(projectGroupCollapseStorageKey.value) || '[]');
        collapsedProjectGroupIds.value = new Set(Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : []);
    } catch {
        collapsedProjectGroupIds.value = new Set();
    }
}

function persistCollapsedProjectGroups() {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(projectGroupCollapseStorageKey.value, JSON.stringify(Array.from(collapsedProjectGroupIds.value)));
    } catch {
        // Local persistence is optional.
    }
}

function toggleProjectGroupCollapse(groupId: string) {
    const next = new Set(collapsedProjectGroupIds.value);
    if (next.has(groupId)) next.delete(groupId);
    else next.add(groupId);
    collapsedProjectGroupIds.value = next;
    persistCollapsedProjectGroups();
}

watch(projectGroupBy, () => {
    collapsedProjectGroupIds.value = new Set();
    draggingProjectGroupId.value = null;
    projectGroupDropTargetId.value = null;
    loadCollapsedProjectGroups();
});

watch(
    () => currentProjectGroupingPreference.value,
    async (groupingValue) => {
        if (typeof route.query.groupBy === 'string' || !groupingValue) return;
        await applyUserDefaultProjectGrouping(groupingValue);
    },
);

watch(
    () => userStore.user?.preferences?.tasks?.defaultProjectGrouping,
    async (groupingValue) => {
        if (currentProjectGroupingPreference.value) return;
        await applyUserDefaultProjectGrouping(groupingValue);
    },
);

const canCreateProjects = computed(() => canMutate.value && !subscription.projectLimitReached.value);
const projectLimitMessage = computed(() => {
    if (!subscription.projectLimitReached.value || subscription.hasUnlimitedProjects.value) {
        return '';
    }

    return `Your ${subscription.subscription.value.name} plan includes up to ${subscription.projectLimit.value} active projects. Upgrade to create another.`;
});

useScopedPageActions(computed(() => canCreateProjects.value
        ? [
        {
            id: 'project-guide',
            label: 'How projects work',
            icon: CircleHelp,
            section: 'Guides',
            action: projectGuide.show,
        },
        {
            id: 'new-project',
            label: 'New project',
            icon: FolderPlus,
            primary: true,
            action: () => openProjectQuickCreate(),
        }]
        : []));

function startProjectFromGuide() {
    projectGuide.complete(() => navigateTo(tenantPath('/projects/new')));
}

function openEditDialog(project: any) {
    void navigateTo(tenantPath(projectSettingsPath(project.slug || project.id)));
}

function openPublishDialog(project: any) {
    publishTargetProject.value = project;
    isPublishDialogOpen.value = true;
}

function openNoticeDialog(project: any) {
    noticeTargetProject.value = project;
    isNoticeDialogOpen.value = true;
}

async function handleArchive(project: any) {
    await projectStore.updateProject(project.id, { status: 'Archived' });
}

async function handleRestore(project: any) {
    await projectStore.updateProject(project.id, { status: 'Active' });
}

async function updateProjectView(value: string) {
    await router.replace({ query: { ...route.query, view: value === 'active' ? undefined : value } });
}

async function handleDelete(project: any) {
    await projectStore.deleteProject(project.id);
}
</script>

<template>
    <div class="page-shell">
        <FeatureGateNotice
            v-if="projectLimitMessage"
            compact
            title="Project limit reached"
            :description="projectLimitMessage"
            requirement="Upgrade for more active projects"
        />

        <div class="overview-metrics-grid xl:grid-cols-3">
            <MetricCard density="compact" label="Active projects" :value="activeProjectsCount" :icon="FolderOpen" :href="tenantPath('/projects')" />
            <MetricCard density="compact" label="Tasks due" :value="tasksDueSoonCount" :icon="Clock3" :href="tenantPath({ path: '/tasks', query: { preset: 'due-soon' } })" />
            <MetricCard density="compact" label="Approvals waiting" :value="approvalsWaitingCount" :icon="CheckCircle2" :href="tenantPath({ path: '/projects', query: { attention: 'approvals' } })" />
        </div>

        <section class="space-y-4">
            <SectionHeader title="All projects" description="Recently updated projects across the studio, ready to jump back into." :icon="FolderOpen">
                <template #actions><div class="app-section-meta hidden sm:inline-flex">{{ filteredProjects.length }} project{{ filteredProjects.length === 1 ? '' : 's' }}</div></template>
            </SectionHeader>

            <div v-if="projectStore.projects.length" class="grid gap-3 sm:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_minmax(190px,0.3fr)_minmax(190px,0.3fr)_auto]">
                <Input v-model="projectSearchQuery" type="search" placeholder="Search projects or tags..." />
                <SearchableSelectMenu
                    v-model="projectTagFilter"
                    :options="projectTagOptions"
                    placeholder="All tags"
                    search-placeholder="Search tags..."
                    allow-clear
                    clear-label="All tags"
                    show-all-by-default
                    variant="form"
                    empty-state-label="No project tags have been created yet."
                />
                <SearchableSelectMenu
                    :model-value="projectView"
                    :options="projectViewOptions"
                    placeholder="Active projects"
                    search-placeholder="Search project views..."
                    variant="form"
                    @update:model-value="(value) => updateProjectView(String(value || 'active'))"
                />
                <Button v-if="hasProjectFilters" type="button" variant="outline" @click="clearProjectFilters">Clear filters</Button>
            </div>

            <EmptyState
                v-if="filteredProjects.length === 0"
                :artwork="projectStore.projects.length === 0"
                artwork-seed="workspace projects"
                :title="projectStore.projects.length ? 'No projects match these filters' : 'Create your first project'"
                :description="projectStore.projects.length ? 'Clear the search or tag filter to bring the rest of your projects back into view.' : 'Bring milestones, tasks, files and client decisions together in one delivery workspace.'"
            >
                <template #action>
                    <Button v-if="projectStore.projects.length === 0" @click="openProjectQuickCreate()">Create project</Button>
                    <Button v-else variant="outline" @click="clearProjectFilters">Clear filters</Button>
                </template>
            </EmptyState>

            <div v-else class="space-y-8">
                <div class="flex flex-wrap items-center gap-2">
                    <SearchableSelectMenu
                        :model-value="projectGroupBy"
                        :options="projectGroupByOptions"
                        placeholder="Group by"
                        search-placeholder="Search grouping..."
                        storage-key="projects-group-by"
                        empty-state-label="Search grouping options."
                        trigger-class="inline-flex h-8 items-center gap-2 rounded-full border border-border/70 bg-card px-2.5 text-left text-sm shadow-sm transition-colors hover:border-primary/30"
                        @update:model-value="(value) => updateProjectGrouping(String(value || 'none'))"
                    >
                        <template #trigger="{ option }">
                            <Layers3 class="size-3.5 text-muted-foreground" />
                            <span class="text-sm" :class="option ? 'text-foreground' : 'text-muted-foreground'">
                                {{ option?.label || 'No grouping' }}
                            </span>
                        </template>
                    </SearchableSelectMenu>
                    <Button
                        v-if="projectGroupBy !== 'none'"
                        type="button"
                        variant="ghost"
                        size="sm"
                        class="h-8 rounded-full px-3 text-xs text-muted-foreground"
                        @click="updateProjectGrouping('none')"
                    >
                        Clear grouping
                    </Button>
                    <span class="text-sm text-muted-foreground">
                        {{ filteredProjects.length }} project{{ filteredProjects.length === 1 ? '' : 's' }}
                    </span>
                </div>

                <template v-if="projectGroupBy === 'none'">
                    <GroupedEntityList>
                        <div v-for="project in filteredProjects" :key="project.id">
                            <div class="md:hidden">
                                <ProjectSummaryCard
                                    :project="project"
                                    :tenant-slug="tenantSlug"
                                    :client-avatar-url="clientAvatarByProjectId[project.id]"
                                    :can-mutate="canMutate"
                                    :stats="projectStatsById[project.id]"
                                    compact
                                    surface="list"
                                    @edit="openEditDialog"
                                    @notice="openNoticeDialog"
                                    @publish="openPublishDialog"
                                    @archive="handleArchive"
                                    @restore="handleRestore"
                                    @delete="handleDelete"
                                />
                            </div>
                            <div class="hidden md:block">
                                <ProjectSummaryCard
                                    :project="project"
                                    :tenant-slug="tenantSlug"
                                    :client-avatar-url="clientAvatarByProjectId[project.id]"
                                    :can-mutate="canMutate"
                                    :stats="projectStatsById[project.id]"
                                    surface="list"
                                    @edit="openEditDialog"
                                    @notice="openNoticeDialog"
                                    @publish="openPublishDialog"
                                    @archive="handleArchive"
                                    @restore="handleRestore"
                                    @delete="handleDelete"
                                />
                            </div>
                        </div>
                    </GroupedEntityList>
                </template>

                <section
                    v-else
                    v-for="section in groupedProjectSections"
                    :key="section.id"
                    class="space-y-4"
                >
                    <div
                        class="flex items-center gap-1.5 rounded-xl px-1.5 py-1 transition-all"
                        :class="projectGroupDropTargetId === section.id ? 'bg-primary/8 ring-1 ring-primary/25 ring-inset' : ''"
                        @dragover.prevent="setProjectGroupDropTarget(section.id)"
                        @drop.prevent="handleProjectGroupDrop(section.id)"
                        @dragleave="projectGroupDropTargetId === section.id ? projectGroupDropTargetId = null : null"
                    >
                        <button
                            type="button"
                            draggable="true"
                            class="inline-flex h-7 w-7 cursor-grab items-center justify-center rounded-md text-muted-foreground/70 transition-colors hover:bg-muted/25 hover:text-muted-foreground active:cursor-grabbing"
                            :aria-label="`Reorder ${section.title}`"
                            @dragstart.stop="startProjectGroupDrag(section.id)"
                            @dragend="draggingProjectGroupId = null; projectGroupDropTargetId = null"
                        >
                            <GripVertical class="size-3.5" />
                        </button>
                        <button
                            type="button"
                            class="flex min-w-0 flex-1 flex-wrap items-center gap-1.5 rounded-xl py-1 text-left transition-colors hover:bg-muted/35"
                            :aria-expanded="!isProjectGroupCollapsed(section.id)"
                            @click="toggleProjectGroupCollapse(section.id)"
                        >
                            <ChevronRight v-if="isProjectGroupCollapsed(section.id)" class="size-4 text-muted-foreground" />
                            <ChevronDown v-else class="size-4 text-muted-foreground" />
                            <span
                                class="size-2.5 rounded-full"
                                :style="{ backgroundColor: projectGroupColor(section) }"
                                aria-hidden="true"
                            />
                            <h3 class="text-base font-semibold tracking-tight text-foreground">{{ section.title }}</h3>
                            <span class="text-sm text-muted-foreground">{{ section.projects.length }}</span>
                        </button>
                    </div>

                    <GroupedEntityList v-if="!isProjectGroupCollapsed(section.id)">
                        <div v-for="project in section.projects" :key="project.id">
                            <div class="md:hidden">
                                <ProjectSummaryCard
                                    :project="project"
                                    :tenant-slug="tenantSlug"
                                    :client-avatar-url="clientAvatarByProjectId[project.id]"
                                    :can-mutate="canMutate"
                                    :stats="projectStatsById[project.id]"
                                    compact
                                    surface="list"
                                    @edit="openEditDialog"
                                    @notice="openNoticeDialog"
                                    @publish="openPublishDialog"
                                    @archive="handleArchive"
                                    @restore="handleRestore"
                                    @delete="handleDelete"
                                />
                            </div>
                            <div class="hidden md:block">
                                <ProjectSummaryCard
                                    :project="project"
                                    :tenant-slug="tenantSlug"
                                    :client-avatar-url="clientAvatarByProjectId[project.id]"
                                    :can-mutate="canMutate"
                                    :stats="projectStatsById[project.id]"
                                    surface="list"
                                    @edit="openEditDialog"
                                    @notice="openNoticeDialog"
                                    @publish="openPublishDialog"
                                    @archive="handleArchive"
                                    @restore="handleRestore"
                                    @delete="handleDelete"
                                />
                            </div>
                        </div>
                    </GroupedEntityList>
                </section>
            </div>
        </section>

        <PublishProjectDialog
            :open="isPublishDialogOpen"
            :project="publishTargetProject"
            @update:open="(value) => { isPublishDialogOpen = value; if (!value) publishTargetProject = null; }"
            @saved="publishTargetProject = null"
        />

        <ProjectNoticeDialog
            :open="isNoticeDialogOpen"
            :project="noticeTargetProject"
            @update:open="(value) => { isNoticeDialogOpen = value; if (!value) noticeTargetProject = null; }"
            @saved="noticeTargetProject = null"
        />

        <FeatureGuideDialog
            v-model="projectGuide.open.value"
            title="How projects organise client delivery"
            description="Choose the right starting structure, shape the work, and review the complete project before creating it."
            :steps="projectGuideSteps"
            start-label="Create my first project"
            @dismiss="projectGuide.dismiss"
            @start="startProjectFromGuide"
        >
            <template #visual="{ step }">
                <FeatureGuideIllustration
                    :icon="projectGuideVisual(step.id).icon"
                    :label="projectGuideVisual(step.id).label"
                    :trail="['Define', 'Plan', 'Deliver']"
                />
            </template>
        </FeatureGuideDialog>
    </div>
</template>
